import { ReactNode, useState } from "react";
import { Button } from "@/components/ui/button";
import { BuilderFooter } from "./BuilderFooter";
import { Eye } from "lucide-react";
import { useI18n } from "@/hooks/useI18n";

type UserRole = "travel_designer" | "traveler" | "admin";

interface BuilderLayoutProps {
  children: ReactNode;
  onPreview?: () => void;
  onSave?: () => void;
  onSaveDraft?: () => void;
  onCancel?: () => void;
  onExit?: () => void;
  onViewTripRequest?: () => void;
  onRejectItinerary?: (note?: string) => void;
  onAcceptItinerary?: (note?: string) => void;
  onUpdateItineraryNote?: (note: string) => Promise<void>;
  showHeader?: boolean;
  showFooter?: boolean;
  onPreviousStep?: () => void;
  onNextStep?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  isRecapStep?: boolean;
  canProceed?: boolean;
  isReadOnly?: boolean;
  isLoading?: boolean;
  role?: UserRole;
  onRequestChanges?: () => void;
  onBookTrip?: () => void;
  totalPrice?: number;
  startDate?: string;
  endDate?: string;
  itineraryStatus?: string;
}

const BuilderLayout = ({
  children,
  onSaveDraft,
  onSave,
  onCancel,
  onExit,
  onViewTripRequest,
  onRejectItinerary,
  onAcceptItinerary,
  onUpdateItineraryNote,
  showHeader = true,
  showFooter = true,
  onPreviousStep,
  onNextStep,
  isFirstStep = false,
  isLastStep = false,
  isRecapStep = false,
  canProceed = true,
  isReadOnly = false,
  isLoading = false,
  role,
  onRequestChanges,
  onBookTrip,
  totalPrice,
  startDate,
  endDate,
  itineraryStatus,
}: BuilderLayoutProps) => {
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const { t } = useI18n();

  const handleSaveDraft = async () => {
    if (!onSaveDraft) return;
    try {
      setIsSavingDraft(true);
      await Promise.resolve(onSaveDraft());
    } catch (err) {
      console.error('Error during save draft', err);
    } finally {
      setIsSavingDraft(false);
    }
  };

  return (
    <div className="h-screen bg-white flex flex-col">
      {/* Header */}
      {showHeader && (
        <header className="flex-shrink-0 border-b border-gray-200 px-8 py-4">
          <div className="flex items-center justify-between">

            <div className="flex items-center gap-3">
              {/* Logo */}
              <div className="flex items-center gap-3">
                <img
                  src="/assets/images/dark_small.svg"
                  alt="Place2Be Logo"
                  className="h-8 w-8"
                />
                <h1 className="text-md font-semibold text-gray-900">Itinerary Builder</h1>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={onViewTripRequest} className="flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  View trip request
                </Button>
              </div>
            </div>

            {/* Action Buttons - Only show if not read-only and not loading */}
            {!isReadOnly && !isLoading && (
              <div className="flex items-center gap-4">
                {onSaveDraft && (
                  <Button onClick={handleSaveDraft} variant="outline" size="sm" className="flex items-center gap-2" disabled={isSavingDraft}>
                    {isSavingDraft ? 'Saving...' : 'Save & exit'}
                  </Button>
                )}
              </div>
            )}
            {isReadOnly && onExit && (
              <Button size="sm" onClick={onExit} variant="outline" className="flex items-center gap-2">
                Exit
              </Button>
            )}
          </div>
        </header>
      )}

      {/* Main Content - Scrollable */}
      <main className="flex-1 overflow-y-auto">
        {children}
      </main>

      {/* Footer */}
      {showFooter && (
        <BuilderFooter
          role={role}
          isReadOnly={isReadOnly}
          isLoading={isLoading}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          isRecapStep={isRecapStep}
          canProceed={canProceed}
          isSavingDraft={isSavingDraft}
          onCancel={onCancel}
          onPreviousStep={onPreviousStep}
          onNextStep={onNextStep}
          onCreateQuote={onSave}
          onRejectItinerary={onRejectItinerary}
          onAcceptItinerary={onAcceptItinerary}
          onUpdateItineraryNote={onUpdateItineraryNote}
          onRequestChanges={onRequestChanges}
          onBookTrip={onBookTrip}
          totalPrice={totalPrice}
          startDate={startDate}
          endDate={endDate}
          itineraryStatus={itineraryStatus}
        />
      )}
      {isSavingDraft && (
        <div className="fixed inset-0 bg-white/60 z-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('itinerary.savingItinerary')}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuilderLayout;
